import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import getDecryptedToken from "../../api/getDecryptedToken";
import baseUrl from "../../api/baseUrl";
import socket from "../../api/socket";
import { CustomRoomPicker } from "./components/CustomRoomPicker";
import { InvalidCustomRoomsLink } from "./components/InvalidCustomRoomsLink";
import { LegacyGlobalQueueBoard } from "./components/LegacyGlobalQueueBoard";
import { ModeLegend } from "./components/ModeLegend";
import { parseFixPaginationFlag, RoomCardsGrid } from "./components/RoomCardsGrid";
import { SideVideoLayoutProvider } from "./components/SideVideoLayoutContext";
import { RoomsLoadingFullScreen } from "./components/RoomsLoadingFullScreen";
import { SingleRoomBoard } from "./components/SingleRoomBoard";
import { useSearchQuery } from "./hooks/useSearchQuery";
import { clinicRoomToBoard, getRoomPathSegment, getTodayWeekDay, mergeRoomWithLive } from "./nextQueueUtils";
import type { QueueBoardRoom } from "./types";
export type { QueueBoardRoom } from "./types";

export default function NextQueue() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const query = useSearchQuery();

  // Fullscreen flash (3s) when the displayed queue number changes.
  // Declared early because the layout wrapper uses it.
  const prevDisplayedQueueRef = useRef<string | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const [queueFlash, setQueueFlash] = useState<{
    show: boolean;
    value: string;
    roomId: string | null;
  }>({
    show: false,
    value: "0",
    roomId: null,
  });
  const lastSpokenQueueRef = useRef<string | null>(null);
  const [voiceStatus, setVoiceStatus] = useState<string>("");
  const [availableVoiceCount, setAvailableVoiceCount] = useState<number>(0);
  const selectedVoiceRef = useRef<SpeechSynthesisVoice | null>(null);
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined" && localStorage.getItem("queueVoiceEnabled") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth) return;

    const pickVoice = () => {
      const voices = synth.getVoices() || [];
      setAvailableVoiceCount(voices.length);
      // Prefer English *female* voices (best-effort; depends on OS/browser installed voices)
      const isEnglish = (v: SpeechSynthesisVoice) => /en/i.test(v.lang);
      const isFemaleName = (name: string) =>
        /female|woman|zira|susan|samantha|victoria|karen|hazel|catherine|serena|aria|jenny|natasha/i.test(
          name
        );

      const preferred =
        voices.find((v) => isEnglish(v) && isFemaleName(v.name)) ||
        voices.find((v) => isEnglish(v) && isFemaleName(v.voiceURI || "")) ||
        voices.find((v) => isEnglish(v) && /google|microsoft/i.test(v.name)) ||
        voices.find((v) => isEnglish(v)) ||
        voices[0] ||
        null;
      selectedVoiceRef.current = preferred;
    };

    pickVoice();
    synth.addEventListener?.("voiceschanged", pickVoice as any);
    return () => {
      synth.removeEventListener?.("voiceschanged", pickVoice as any);
    };
  }, []);

  const speakQueue = useCallback((value: string, roomNo?: string | null) => {
    try {
      if (typeof window === "undefined") return;
      if (!voiceEnabled) return;
      const synth = window.speechSynthesis;
      if (!synth) {
        setVoiceStatus("Speech synthesis not supported");
        return;
      }
      try {
        synth.resume?.();
      } catch {
        // ignore
      }
      const room = String(roomNo ?? "").trim();
      const token = String(value ?? "").trim();
      if (!token) return;
      // Exact phrase: "token number (no) , room number (no)" (comma adds a speech pause)
      const text = room
        ? `token number ${token} , room number ${room}`
        : `token number ${token}`;
      // Avoid repeating the same value rapidly
      if (lastSpokenQueueRef.current === text) return;
      lastSpokenQueueRef.current = text;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9;
      u.pitch = 1.0;
      u.volume = 1.0;
      u.lang = "en-US";
      if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;
      u.onstart = () => setVoiceStatus(`Speaking: ${text}`);
      u.onend = () => setVoiceStatus("Spoken");
      u.onerror = () => setVoiceStatus("Speech error (browser blocked?)");
      synth.speak(u);
    } catch {
      // ignore (some browsers block speech without user gesture)
    }
  }, [voiceEnabled]);

  const enableVoice = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      // Warm up speech synthesis in a user-gesture handler
      const synth = window.speechSynthesis;
      if (synth) {
        synth.cancel();
        try {
          synth.resume?.();
        } catch {
          // ignore
        }
        const u = new SpeechSynthesisUtterance("Voice enabled");
        u.rate = 1;
        u.pitch = 1;
        u.volume = 1;
        u.lang = "en-US";
        if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;
        u.onend = () => setVoiceStatus("Voice enabled (test spoken)");
        synth.speak(u);
      }
      localStorage.setItem("queueVoiceEnabled", "1");
      setVoiceEnabled(true);
    } catch {
      setVoiceEnabled(true);
    }
  }, []);

  const testVoice = useCallback(() => {
    speakQueue("1", "1");
  }, [speakQueue]);

  // Always show video on the right side (split-screen).
  // Source priority:
  // 1) Clinic uploaded queue videos (S3 signed URLs) - rotates
  // 2) Optional env `REACT_APP_SIDE_VIDEO_URL` (youtube/vimeo/mp4)
  const [queueVideoUrls, setQueueVideoUrls] = useState<string[]>([]);
  const [queueVideoIdx, setQueueVideoIdx] = useState(0);
  const envFallbackSrc = (process.env.REACT_APP_SIDE_VIDEO_URL ?? "").trim();
  const rawSideVideoSrc = queueVideoUrls[queueVideoIdx] || envFallbackSrc;
  const showSideVideo = rawSideVideoSrc !== "";

  const toYoutubeEmbedUrl = (url: string) => {
    // Accepts:
    // - https://youtu.be/<id>
    // - https://www.youtube.com/watch?v=<id>
    // - https://www.youtube.com/embed/<id>
    const trimmed = url.trim();
    const match =
      trimmed.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/i) ??
      trimmed.match(/[?&]v=([A-Za-z0-9_-]{6,})/i) ??
      trimmed.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/i);
    const id = match?.[1];
    if (!id) return trimmed;
    // loop works only with playlist param set to the same id
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&rel=0&modestbranding=1`;
  };

  const toVimeoEmbedUrl = (url: string) => {
    const trimmed = url.trim();
    const match =
      trimmed.match(/vimeo\.com\/(\d{6,})/i) ?? trimmed.match(/player\.vimeo\.com\/video\/(\d{6,})/i);
    const id = match?.[1];
    if (!id) return trimmed;
    return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1`;
  };

  const isProbablyEmbedUrl = (url: string) =>
    /youtube\.com\/embed|player\.vimeo\.com\/video/i.test(url) || url.includes("embed");

  const sideVideoSrc = useMemo(() => {
    if (!rawSideVideoSrc) return "";
    // Signed S3 URLs should be used as-is
    if (/^https?:\/\//i.test(rawSideVideoSrc) && /X-Amz-/i.test(rawSideVideoSrc)) {
      return rawSideVideoSrc;
    }
    if (/youtu\.be|youtube\.com/i.test(rawSideVideoSrc)) return toYoutubeEmbedUrl(rawSideVideoSrc);
    if (/vimeo\.com/i.test(rawSideVideoSrc)) return toVimeoEmbedUrl(rawSideVideoSrc);
    return rawSideVideoSrc;
  }, [rawSideVideoSrc]);

  // Smooth cross-fade between direct videos (S3 mp4/webm).
  // We keep two <video> elements mounted and crossfade opacity only after the next video is ready.
  const videoRef0 = useRef<HTMLVideoElement | null>(null);
  const videoRef1 = useRef<HTMLVideoElement | null>(null);
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
  const [slotSrc, setSlotSrc] = useState<[string, string]>(() => [sideVideoSrc, ""]);
  const transitionMs = 650;
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [snapshotOn, setSnapshotOn] = useState(false);
  const snapshotFadeMs = 220;
  const snapshotHoldMs = 80;

  const sleep = useCallback((ms: number) => new Promise<void>((r) => window.setTimeout(r, ms)), []);
  const isSwitchingRef = useRef(false);
  const queueVideoIdxRef = useRef(queueVideoIdx);
  const activeSlotRef = useRef<0 | 1>(activeSlot);

  useEffect(() => {
    queueVideoIdxRef.current = queueVideoIdx;
  }, [queueVideoIdx]);

  useEffect(() => {
    activeSlotRef.current = activeSlot;
  }, [activeSlot]);
  const waitForFirstFrame = useCallback((el: HTMLVideoElement) => {
    // Wait until the browser has actually rendered at least one frame.
    // This avoids the "black flash" some browsers show right after play().
    return new Promise<void>((resolve) => {
      const anyEl = el as any;
      if (typeof anyEl?.requestVideoFrameCallback === "function") {
        anyEl.requestVideoFrameCallback(() => resolve());
        return;
      }

      // Fallback: wait until playback time advances or we see a timeupdate.
      const start = el.currentTime;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        el.removeEventListener("timeupdate", onTime);
        resolve();
      };
      const onTime = () => {
        if (el.currentTime > start) finish();
      };
      el.addEventListener("timeupdate", onTime);
      window.setTimeout(finish, 250); // best-effort cap
    });
  }, []);

  const captureFrameToCanvas = useCallback((videoEl: HTMLVideoElement | null) => {
    if (!videoEl) return;
    const c = snapshotCanvasRef.current;
    if (!c) return;
    const vw = videoEl.videoWidth || 0;
    const vh = videoEl.videoHeight || 0;
    if (vw <= 0 || vh <= 0) return;

    const ctx = c.getContext("2d");
    if (!ctx) return;
    c.width = vw;
    c.height = vh;
    try {
      ctx.drawImage(videoEl, 0, 0, vw, vh);
    } catch {
      // ignore (rare: cross-origin / draw errors)
    }
  }, []);

  const triggerNextVideo = useCallback(
    async (currentSlot: 0 | 1) => {
      if (queueVideoUrls.length <= 1) return;
      if (isSwitchingRef.current) return;
      isSwitchingRef.current = true;

      const currentIdx = queueVideoIdxRef.current;
      const nextIdx = (currentIdx + 1) % queueVideoUrls.length;
      const nextUrl = queueVideoUrls[nextIdx] || "";
      const nextSlot: 0 | 1 = currentSlot === 0 ? 1 : 0;
      const nextEl = nextSlot === 0 ? videoRef0.current : videoRef1.current;
      const prevEl = currentSlot === 0 ? videoRef0.current : videoRef1.current;
      if (!nextEl) {
        isSwitchingRef.current = false;
        return;
      }
      if (!nextUrl) {
        isSwitchingRef.current = false;
        return;
      }

      try {
        // Freeze the last visible frame so any end-of-video black frame is never shown.
        captureFrameToCanvas(prevEl);
        setSnapshotOn(true);

        // Swap the source in the hidden slot and hard-reset playback.
        // IMPORTANT: set src on the element immediately (avoid waiting for React render),
        // otherwise `load()` can run against a stale/empty src and the playlist stops.
        setSlotSrc((prev) => {
          const next: [string, string] = [...prev] as any;
          next[nextSlot] = nextUrl;
          return next;
        });
        try {
          nextEl.pause();
        } catch {
          // ignore
        }
        try {
          nextEl.src = nextUrl;
        } catch {
          // ignore
        }
        try {
          nextEl.currentTime = 0;
        } catch {
          // ignore
        }
        nextEl.muted = true;
        nextEl.playsInline = true;
        nextEl.preload = "auto";
        try {
          nextEl.load();
        } catch {
          // ignore
        }

        await sleep(snapshotFadeMs);

        const waitReady = () =>
          new Promise<void>((resolve) => {
            if (nextEl.readyState >= 2) {
              resolve();
              return;
            }
            const done = () => resolve();
            nextEl.addEventListener("canplay", done, { once: true });
            nextEl.addEventListener("loadeddata", done, { once: true });
            nextEl.addEventListener("error", done, { once: true });
          });

        await waitReady();
        try {
          await nextEl.play();
        } catch {
          // ignore (muted autoplay should succeed)
        }
        await waitForFirstFrame(nextEl);

        // Switch to next while snapshot is covering.
        setActiveSlot(nextSlot);
        setQueueVideoIdx(nextIdx);

        // Stop the previous video so it never reaches the "ended black frame".
        try {
          prevEl?.pause();
        } catch {
          // ignore
        }

        await sleep(snapshotHoldMs);
        setSnapshotOn(false);
      } finally {
        window.setTimeout(() => {
          isSwitchingRef.current = false;
        }, transitionMs);
      }
    },
    [
      captureFrameToCanvas,
      queueVideoUrls,
      sleep,
      snapshotFadeMs,
      snapshotHoldMs,
      transitionMs,
      waitForFirstFrame,
    ]
  );

  // Ensure active slot always reflects the current selected video source.
  useEffect(() => {
    setSlotSrc((prev) => {
      const next: [string, string] = [...prev] as any;
      next[activeSlot] = sideVideoSrc;
      return next;
    });
  }, [activeSlot, sideVideoSrc]);

  // Preload the *next* playlist video to reduce buffering.
  // (Only applies to direct video URLs, not YouTube/Vimeo embeds.)
  useEffect(() => {
    if (queueVideoUrls.length <= 1) return;
    const currentUrl = queueVideoUrls[queueVideoIdx] || "";
    // If current is an embed URL, preloading next won't help here.
    if (isProbablyEmbedUrl(currentUrl)) return;

    const nextIdx = (queueVideoIdx + 1) % queueVideoUrls.length;
    const nextUrl = queueVideoUrls[nextIdx] || "";
    if (!nextUrl || isProbablyEmbedUrl(nextUrl)) return;

    const v = document.createElement("video");
    v.preload = "auto";
    v.muted = true;
    v.playsInline = true;
    v.src = nextUrl;
    try {
      v.load();
    } catch {
      // ignore
    }
    // best-effort cleanup
    return () => {
      try {
        v.removeAttribute("src");
        v.load();
      } catch {
        // ignore
      }
    };
  }, [queueVideoIdx, queueVideoUrls]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await baseUrl.get("/client/setting/queueVideo/list");
        const list = res?.data?.data;
        const urls =
          Array.isArray(list)
            ? list
                .map((v: any) => String(v?.url || "").trim())
                .filter((u: string) => u !== "")
            : [];
        if (!cancelled) setQueueVideoUrls(urls);
      } catch {
        if (!cancelled) setQueueVideoUrls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // If list changes (upload/delete), keep index in range.
    if (queueVideoUrls.length === 0) {
      setQueueVideoIdx(0);
      return;
    }
    setQueueVideoIdx((prev) => (prev < queueVideoUrls.length ? prev : 0));
  }, [queueVideoUrls.length]);

  const QueueAnnounceOverlays = useCallback(
    () => (
      <>
        {!voiceEnabled ? (
          <div
            style={{
              position: "fixed",
              left: 12,
              bottom: 12,
              zIndex: 10000,
              background: "rgba(15, 23, 42, 0.88)",
              color: "#fff",
              padding: "10px 12px",
              borderRadius: 12,
              boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              maxWidth: 520,
            }}
          >
            <div style={{ fontSize: 12, lineHeight: 1.3, minWidth: 0 }}>
              Click <b>Enable voice</b> once to allow announcements.
            </div>
            <button
              type="button"
              onClick={enableVoice}
              style={{
                background: "#4f46e5",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Enable voice
            </button>
          </div>
        ) : null}

        {queueFlash.show ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "#0b1220",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div style={{ textAlign: "center" }}>
              {queueFlash.roomId ? (
                <div
                  style={{
                    fontSize: "min(10vw, 72px)",
                    lineHeight: 1.1,
                    fontWeight: 800,
                    letterSpacing: -1,
                    color: "rgba(255,255,255,0.92)",
                    marginBottom: "min(4vw, 32px)",
                    textShadow: "0 10px 40px rgba(0,0,0,0.35)",
                  }}
                >
                  Room number {queueFlash.roomId}
                </div>
              ) : null}
              <div
                style={{
                  fontSize: "min(28vw, 220px)",
                  lineHeight: 1,
                  fontWeight: 900,
                  letterSpacing: -2,
                  color: "#ffffff",
                  textShadow: "0 18px 60px rgba(0,0,0,0.35)",
                }}
              >
                {queueFlash.value}
              </div>
            </div>
          </div>
        ) : null}
      </>
    ),
    [voiceEnabled, enableVoice, queueFlash.show, queueFlash.value, queueFlash.roomId]
  );

  const SideVideoLayout = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (!showSideVideo) return <>{children}</>;

      // 30% queue + 70% video (desktop split screen)
      const leftWidth = "30%";
      const asideWidth = "70%";
      const embed = isProbablyEmbedUrl(sideVideoSrc);
      const canCrossFade = !embed && queueVideoUrls.length > 1;

      return (
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            padding: 12,
            minHeight: "100vh",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: leftWidth,
              minWidth: 320,
              flex: "0 0 auto",
              alignSelf: "stretch",
              minHeight: 0,
              height: "calc(100vh - 24px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SideVideoLayoutProvider active>{children}</SideVideoLayoutProvider>
          </div>

          <aside
            style={{
              width: asideWidth,
              minWidth: 520,
              position: "sticky",
              top: 12,
              alignSelf: "flex-start",
              height: "calc(100vh - 24px)",
              borderRadius: 12,
              overflow: "hidden",
              background: "#000",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            {embed ? (
              <iframe
                title="Side video"
                src={sideVideoSrc}
                style={{ width: "100%", height: "100%", border: 0 }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : canCrossFade ? (
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                {/* Snapshot overlay prevents any end-of-video black frame flash */}
                <canvas
                  ref={snapshotCanvasRef}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: snapshotOn ? 1 : 0,
                    transition: `opacity ${snapshotFadeMs}ms ease-in-out`,
                    pointerEvents: "none",
                    zIndex: 3,
                  }}
                />
                <video
                  ref={videoRef0}
                  src={slotSrc[0]}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: activeSlot === 0 ? 1 : 0,
                    transition: `opacity ${transitionMs}ms ease-in-out`,
                  }}
                  autoPlay
                  muted
                  preload="auto"
                  playsInline
                  onTimeUpdate={
                    activeSlot === 0
                      ? () => {
                          const el = videoRef0.current;
                          if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
                          const remaining = el.duration - el.currentTime;
                          if (remaining <= 0.15) triggerNextVideo(0);
                        }
                      : undefined
                  }
                  onEnded={activeSlot === 0 ? () => triggerNextVideo(0) : undefined}
                />
                <video
                  ref={videoRef1}
                  src={slotSrc[1]}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: activeSlot === 1 ? 1 : 0,
                    transition: `opacity ${transitionMs}ms ease-in-out`,
                  }}
                  autoPlay
                  muted
                  preload="auto"
                  playsInline
                  onTimeUpdate={
                    activeSlot === 1
                      ? () => {
                          const el = videoRef1.current;
                          if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
                          const remaining = el.duration - el.currentTime;
                          if (remaining <= 0.15) triggerNextVideo(1);
                        }
                      : undefined
                  }
                  onEnded={activeSlot === 1 ? () => triggerNextVideo(1) : undefined}
                />
              </div>
            ) : (
              <video
                src={sideVideoSrc}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                autoPlay
                muted
                loop={queueVideoUrls.length <= 1}
                preload="auto"
                playsInline
                onEnded={() => {
                  // For a playlist (2+ videos), move to the next and wrap back to the first.
                  if (queueVideoUrls.length <= 1) return;
                  setQueueVideoIdx((prev) => (prev + 1) % queueVideoUrls.length);
                }}
              />
            )}
          </aside>
        </div>
      );
    },
    [
      showSideVideo,
      sideVideoSrc,
      queueVideoUrls.length,
      activeSlot,
      snapshotOn,
      snapshotFadeMs,
      slotSrc,
      queueVideoIdx,
      queueVideoUrls,
      sleep,
      waitForFirstFrame,
      triggerNextVideo,
    ]
  );

  const withQueueBoard = useCallback(
    (node: React.ReactNode) => (
      <>
        <QueueAnnounceOverlays />
        <SideVideoLayout>{node}</SideVideoLayout>
      </>
    ),
    [QueueAnnounceOverlays, SideVideoLayout]
  );

  const clinicName = getDecryptedToken("clinicName") || "";
  const clinicLogo = getDecryptedToken("clinicLogo");
  const showLogo = getDecryptedToken("logo") === "true";
  const hasClinicLogo = showLogo && String(clinicLogo ?? "").trim() !== "";
  const clinicLogoUrl = `${process.env.REACT_APP_API}${clinicLogo}`;

  const view = (query.get("view") || "").trim().toLowerCase();
  const roomQuery = query.get("room") || query.get("roomId");
  const roomsParam = query.get("rooms");
  const sessionNameParam = (query.get("sessionName") || query.get("sessionname") || "").trim();

  /** Second URL segment after /room/ — works for both /room/:roomId and static /room/all */
  const pathSecondRaw = getRoomPathSegment(pathname)?.trim();
  const pathSecondLower = pathSecondRaw?.toLowerCase();
  const reservedPath = pathSecondLower === "custom" || pathSecondLower === "all";
  const pathRoomId = pathSecondRaw && !reservedPath ? pathSecondRaw : undefined;
  const roomQueryNorm = roomQuery?.trim();
  const roomQueryLower = roomQueryNorm?.toLowerCase();
  const roomQueryId =
    roomQueryNorm && roomQueryLower !== "custom" && roomQueryLower !== "all" ? roomQueryNorm : "";
  /** Path segment wins over ?room= (same as before) */
  const effectiveRoomId = pathRoomId || roomQueryId || "";

  const [pickerSelection, setPickerSelection] = useState<Set<string>>(() => new Set());

  const [clinicRooms, setClinicRooms] = useState<QueueBoardRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);

  const loadClinicRooms = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setRoomsLoading(true);
      try {
        const params: Record<string, string> = {};
        if (sessionNameParam) {
          params.sessionName = sessionNameParam;
          params.day = getTodayWeekDay();
        }
        const res = await baseUrl.get("/client/setting/clinicRoom/getRooms", {
          params,
        });
        const list = res?.data?.data;
        if (!Array.isArray(list)) {
          setClinicRooms([]);
          return;
        }
        const mapped: QueueBoardRoom[] = [];
        for (const row of list) {
          const b = clinicRoomToBoard(row);
          if (b) mapped.push(b);
        }
        setClinicRooms(mapped);
      } catch {
        setClinicRooms([]);
      } finally {
        if (!opts?.silent) setRoomsLoading(false);
      }
    },
    [sessionNameParam]
  );

  useEffect(() => {
    void loadClinicRooms();
  }, [loadClinicRooms]);

  useEffect(() => {
    const refreshRooms = () => {
      void loadClinicRooms({ silent: true });
    };

    const onRoomAllocationUpdate = (evt: { data?: { day?: string } }) => {
      const evtDay = String(evt?.data?.day ?? "").trim();
      const today = getTodayWeekDay();
      if (evtDay && evtDay.toLowerCase() !== today.toLowerCase()) return;
      refreshRooms();
    };

    const onAppointmentListRefresh = (evt: { data?: { source?: string } }) => {
      if (String(evt?.data?.source ?? "") === "roomAllocation") {
        refreshRooms();
      }
    };

    socket.on("roomAllocationUpdate", onRoomAllocationUpdate);
    socket.on("uppdateAppointment", onAppointmentListRefresh);
    return () => {
      socket.off("roomAllocationUpdate", onRoomAllocationUpdate);
      socket.off("uppdateAppointment", onAppointmentListRefresh);
    };
  }, [loadClinicRooms]);

  const [liveRoomQueues, setLiveRoomQueues] = useState<Record<string, number>>({});
  const [liveGlobalQueue, setLiveGlobalQueue] = useState<number | null>(null);
  const [socketQueueEvent, setSocketQueueEvent] = useState<{
    queue: number;
    roomId: string | null;
    seq: number;
  } | null>(null);
  const socketQueueSeqRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!socket.connected && localStorage.getItem("token")) {
      socket.connect();
    }
  }, []);

  useEffect(() => {
    const raw = typeof window !== "undefined" ? localStorage.getItem("nextQueue") : null;
    if (raw == null || String(raw).trim() === "") return;
    const n = parseInt(String(raw).trim(), 10);
    if (Number.isFinite(n) && n >= 0) setLiveGlobalQueue(Math.floor(n));
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "nextQueue" || e.newValue == null) return;
      const n = parseInt(e.newValue, 10);
      if (Number.isFinite(n) && n >= 0) setLiveGlobalQueue(Math.floor(n));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const handler = (evt: {
      data?: {
        status?: string;
        queue?: number | string;
        roomNumber?: string | number;
        room?: string | number;
        sessionName?: string;
      };
      token?: { clinicId?: string };
    }) => {
      const myClinic = getDecryptedToken("cid");
      const evtClinic = evt?.token?.clinicId != null ? String(evt.token.clinicId) : undefined;
      if (myClinic && evtClinic && myClinic !== evtClinic) return;

      const d = evt?.data;
      if (!d || String(d.status).toLowerCase() !== "ongoing") return;

      if (sessionNameParam) {
        const evtSession = String(d.sessionName ?? "").trim();
        if (
          !evtSession ||
          evtSession.toLowerCase() !== sessionNameParam.toLowerCase()
        ) {
          return;
        }
      }

      const rawQ = d.queue;
      const q = rawQ != null && rawQ !== "" ? Number(rawQ) : NaN;
      if (!Number.isFinite(q) || q < 0) return;

      const qn = Math.floor(q);
      setLiveGlobalQueue(qn);

      const rn = String(d.roomNumber ?? d.room ?? "").trim();
      if (rn) {
        setLiveRoomQueues((prev) => ({ ...prev, [rn]: qn }));
      }

      socketQueueSeqRef.current += 1;
      setSocketQueueEvent({ queue: qn, roomId: rn || null, seq: socketQueueSeqRef.current });
    };
    socket.on("allAppointmentStatusChange", handler);
    return () => {
      socket.off("allAppointmentStatusChange", handler);
    };
  }, [sessionNameParam]);

  const toggleRoom = useCallback((id: string) => {
    setPickerSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openCustomDisplay = useCallback(() => {
    if (pickerSelection.size === 0) return;
    const ids = Array.from(pickerSelection).sort().join(",");
    const params: Record<string, string> = { rooms: ids };
    if (sessionNameParam) params.sessionName = sessionNameParam;
    const search = createSearchParams(params).toString();
    navigate({ pathname: "/room/custom", search: search ? `?${search}` : "" }, { replace: true });
  }, [navigate, pickerSelection, sessionNameParam]);

  const selectedRoomsFromQuery = useMemo(() => {
    if (!roomsParam?.trim()) return [];
    const ids = roomsParam.split(",").map((s: string) => s.trim()).filter(Boolean);
    const map = new Map(clinicRooms.map((r) => [r.id, r]));
    return ids.map((id: string) => map.get(id)).filter(Boolean) as QueueBoardRoom[];
  }, [roomsParam, clinicRooms]);

  const singleRoom = useMemo(() => {
    if (!effectiveRoomId) return null;
    const key = effectiveRoomId.trim();
    return clinicRooms.find((r) => r.id === key) || null;
  }, [effectiveRoomId, clinicRooms]);

  const roomsAllLive = useMemo(
    () => clinicRooms.map((r) => mergeRoomWithLive(r, liveRoomQueues)),
    [clinicRooms, liveRoomQueues]
  );

  const selectedRoomsLive = useMemo(
    () => selectedRoomsFromQuery.map((r) => mergeRoomWithLive(r, liveRoomQueues)),
    [selectedRoomsFromQuery, liveRoomQueues]
  );

  const singleRoomLive = useMemo(
    () => (singleRoom ? mergeRoomWithLive(singleRoom, liveRoomQueues) : null),
    [singleRoom, liveRoomQueues]
  );

  const applySessionLabel = useCallback(
    (room: QueueBoardRoom): QueueBoardRoom => {
      if (!sessionNameParam) return room;
      return { ...room, name: sessionNameParam };
    },
    [sessionNameParam]
  );

  const filterRoomsForSession = useCallback(
    (rooms: QueueBoardRoom[]): QueueBoardRoom[] => {
      if (!sessionNameParam) return rooms;
      return rooms.filter((r) => {
        const doc = String(r.doctorName ?? "").trim();
        return doc && doc !== "—";
      });
    },
    [sessionNameParam]
  );

  const singleRoomDisplay = useMemo(
    () => (singleRoomLive ? applySessionLabel(singleRoomLive) : null),
    [singleRoomLive, applySessionLabel]
  );

  const selectedRoomsDisplay = useMemo(
    () => filterRoomsForSession(selectedRoomsLive.map(applySessionLabel)),
    [selectedRoomsLive, applySessionLabel, filterRoomsForSession]
  );

  const roomsAllDisplay = useMemo(
    () => filterRoomsForSession(roomsAllLive.map(applySessionLabel)),
    [roomsAllLive, applySessionLabel, filterRoomsForSession]
  );

  /* Legacy: queue from query/storage when using old single-number screen (no room concept) */
  const queueFromQuery = query.get("q") ?? query.get("queue") ?? query.get("n");
  const queueFromStorage = typeof window !== "undefined" ? localStorage.getItem("nextQueue") : null;
  const legacyQueueText = useMemo(() => {
    const raw = (queueFromQuery ?? queueFromStorage ?? "").toString().trim();
    if (raw === "") return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    return String(n);
  }, [queueFromQuery, queueFromStorage]);

  /** `/room/custom` or `?view=custom` */
  const isCustomRoute = view === "custom" || pathSecondLower === "custom";

  /** `/room/all` or `?view=all` */
  const isAllRoute = view === "all" || pathSecondLower === "all";

  const visibleRoomIds = useMemo(() => {
    if (effectiveRoomId.trim()) return new Set([effectiveRoomId.trim()]);
    if (isCustomRoute && roomsParam?.trim()) {
      return new Set(selectedRoomsFromQuery.map((r) => r.id));
    }
    // Session / all-room boards must keep room ids for speech even if localStorage
    // nextQueue is set (legacyQueueText). Pure legacy single-token screens stay empty.
    if (
      isAllRoute ||
      Boolean(sessionNameParam) ||
      (!effectiveRoomId && !legacyQueueText && view !== "single")
    ) {
      return new Set(clinicRooms.map((r) => r.id));
    }
    return new Set<string>();
  }, [
    effectiveRoomId,
    isCustomRoute,
    isAllRoute,
    roomsParam,
    selectedRoomsFromQuery,
    clinicRooms,
    legacyQueueText,
    sessionNameParam,
    view,
  ]);

  /** Multi-room grid — announcements must be per room, not global queue only. */
  const isMultiRoomBoard = useMemo(() => {
    if (effectiveRoomId.trim()) return false;
    if (isCustomRoute && !roomsParam?.trim()) return false;
    if (view === "single") return false;
    return visibleRoomIds.size > 0;
  }, [effectiveRoomId, isCustomRoute, roomsParam, view, visibleRoomIds]);

  const isVisibleRoom = useCallback(
    (roomId: string) => {
      const id = roomId.trim();
      if (!id) return false;
      if (visibleRoomIds.has(id)) return true;
      const lower = id.toLowerCase();
      return Array.from(visibleRoomIds).some((visibleId) => visibleId.toLowerCase() === lower);
    },
    [visibleRoomIds]
  );

  const fireQueueFlash = useCallback(
    (queue: string, roomId: string | null) => {
      const q = String(queue ?? "").trim();
      const rn = String(roomId ?? "").trim();
      const dedupeKey = rn ? `${rn}:${q}` : q;
      if (prevDisplayedQueueRef.current === dedupeKey) return;
      prevDisplayedQueueRef.current = dedupeKey;

      setQueueFlash({ show: true, value: q, roomId: rn || null });
      // Speak immediately with the same room/token shown on the flash
      speakQueue(q, rn || null);

      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => {
        setQueueFlash((s) => ({ ...s, show: false }));
        flashTimerRef.current = null;
      }, 3000);
    },
    [speakQueue]
  );

  // Fullscreen flash when the *displayed* queue number changes.
  const displayedQueueNumber = useMemo(() => {
    if (effectiveRoomId) {
      return singleRoomDisplay ? String(singleRoomDisplay.currentQueue ?? "") : null;
    }
    // Legacy single-number board
    const q = liveGlobalQueue !== null ? String(liveGlobalQueue) : legacyQueueText;
    return q ?? null;
  }, [effectiveRoomId, singleRoomDisplay, liveGlobalQueue, legacyQueueText]);

  // Multi-room: announce from socket per room (always include room number).
  useEffect(() => {
    if (!socketQueueEvent || !isMultiRoomBoard) return;

    const { queue, roomId } = socketQueueEvent;
    const q = String(queue);
    const rn = String(roomId ?? "").trim();
    if (!rn) return;
    if (!isVisibleRoom(rn)) return;

    fireQueueFlash(q, rn);
  }, [socketQueueEvent, isMultiRoomBoard, isVisibleRoom, fireQueueFlash]);

  // Single-room board: follow displayed queue value (room id from URL).
  useEffect(() => {
    if (isMultiRoomBoard) return;

    const next = displayedQueueNumber?.trim() || null;
    const roomId = effectiveRoomId.trim() || null;
    const dedupeKey = next ? (roomId ? `${roomId}:${next}` : next) : null;
    const prev = prevDisplayedQueueRef.current;

    if (next == null || next === "" || dedupeKey == null) return;

    if (prev == null || prev === "") {
      prevDisplayedQueueRef.current = dedupeKey;
      return;
    }

    fireQueueFlash(next, roomId);
  }, [displayedQueueNumber, isMultiRoomBoard, effectiveRoomId, fireQueueFlash]);

  /* ---------- Route modes ---------- */

  const showDebugLegend = query.get("debug") === "1";
  /** `?fix=1` or `?fixed=1` — max 8 doctor cards per page with auto-rotate */
  const fixPagination = parseFixPaginationFlag(query);

  const needsRoomCatalog =
    isAllRoute ||
    (isCustomRoute && Boolean(roomsParam?.trim())) ||
    Boolean(String(effectiveRoomId || "").trim()) ||
    (!String(effectiveRoomId || "").trim() && !legacyQueueText && view !== "single");

  const onChooseRooms = useCallback(() => {
    navigate({ pathname: "/room/custom", search: "" }, { replace: true });
  }, [navigate]);

  if (roomsLoading && needsRoomCatalog) {
    return (
      <>
        <RoomsLoadingFullScreen />
        {showDebugLegend ? <ModeLegend /> : null}
      </>
    );
  }

  if (isCustomRoute) {
    const hasRoomsParam = Boolean(roomsParam?.trim());
    if (hasRoomsParam && selectedRoomsFromQuery.length > 0) {
      return withQueueBoard(
        <>
          <RoomCardsGrid
            clinicName={clinicName}
            rooms={selectedRoomsDisplay}
            fixPagination={fixPagination}
          />
          {showDebugLegend ? <ModeLegend /> : null}
        </>
      );
    }
    if (hasRoomsParam && selectedRoomsFromQuery.length === 0) {
      return withQueueBoard(
        <>
          <InvalidCustomRoomsLink onChooseRooms={onChooseRooms} />
          {showDebugLegend ? <ModeLegend /> : null}
        </>
      );
    }
    return withQueueBoard(
      <>
        <CustomRoomPicker
          clinicName={clinicName}
          rooms={clinicRooms}
          roomsLoading={roomsLoading}
          selectedIds={pickerSelection}
          onToggle={toggleRoom}
          onDisplay={openCustomDisplay}
        />
        {showDebugLegend ? <ModeLegend /> : null}
      </>
    );
  }

  if (isAllRoute) {
    return withQueueBoard(
      <>
        <RoomCardsGrid
          clinicName={clinicName}
          rooms={roomsAllDisplay}
          fixPagination={fixPagination}
        />
        {showDebugLegend ? <ModeLegend /> : null}
      </>
    );
  }

  if (effectiveRoomId && singleRoomDisplay) {
    return withQueueBoard(
      <SingleRoomBoard
        clinicName={clinicName}
        clinicLogoUrl={clinicLogoUrl}
        hasClinicLogo={hasClinicLogo}
        room={singleRoomDisplay}
      />
    );
  }

  /* Fallback: unknown room id — show first mock room as placeholder */
  if (effectiveRoomId && !singleRoom) {
    return withQueueBoard(
      <SingleRoomBoard
        clinicName={clinicName}
        clinicLogoUrl={clinicLogoUrl}
        hasClinicLogo={hasClinicLogo}
        room={applySessionLabel({
          id: effectiveRoomId,
          name: `Room ${effectiveRoomId}`,
          doctorName: "—",
          currentQueue:
            liveRoomQueues[String(effectiveRoomId).trim()] ??
            liveGlobalQueue ??
            (legacyQueueText ? parseInt(legacyQueueText, 10) || 0 : 0),
        })}
      />
    );
  }

  /* Default /room : all rooms board (also when sessionName is set, even if nextQueue exists) */
  if (
    !effectiveRoomId &&
    view !== "single" &&
    (Boolean(sessionNameParam) || !legacyQueueText)
  ) {
    return withQueueBoard(
      <>
        <RoomCardsGrid
          clinicName={clinicName}
          rooms={roomsAllDisplay}
          fixPagination={fixPagination}
        />
        {showDebugLegend ? <ModeLegend /> : null}
      </>
    );
  }

  const queueText =
    liveGlobalQueue !== null ? String(liveGlobalQueue) : (legacyQueueText ?? "0");

  return withQueueBoard(
    <LegacyGlobalQueueBoard
      clinicName={clinicName}
      clinicLogoUrl={clinicLogoUrl}
      hasClinicLogo={hasClinicLogo}
      queueText={queueText}
    />
  );
}
